import { useEffect, useState } from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { Close as RemoveIcon, PictureAsPdfOutlined as PdfIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import endpoints from "../../Helpers/Endpoints";
import { apiGetRequest } from "../../Helpers/Requests";
import { agentChatStyles as styles } from "./styles";

// A file picked in the chat (COMY1-392): a thumbnail for a photo, an icon for a
// PDF. The file is read from the API with the person's session (only its owner
// gets it) and shown from a local object URL.

const thumbs = new Map();

function useThumb(attachment, conversationId) {
  const local = attachment.previewUrl ?? null;
  const [url, setUrl] = useState(local ?? thumbs.get(attachment.id) ?? null);
  useEffect(() => {
    if (local || attachment.kind !== "image" || !attachment.id || !conversationId || thumbs.has(attachment.id)) return undefined;
    let cancelled = false;
    apiGetRequest(endpoints.agentAttachment(attachment.id, conversationId), { responseType: "blob" })
      .then(({ data }) => {
        const objectUrl = URL.createObjectURL(data);
        thumbs.set(attachment.id, objectUrl);
        if (!cancelled) setUrl(objectUrl);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [attachment.id, attachment.kind, conversationId, local]);
  return url;
}

export default function AgentAttachment({ attachment, conversationId, onRemove, size = 56 }) {
  const { t } = useTranslation("agent");
  const url = useThumb(attachment, conversationId);
  return (
    <Box sx={styles.attachment(size)} title={attachment.name}>
      {attachment.kind === "image" && url ? (
        <Box component="img" src={url} alt={attachment.name} sx={styles.attachmentImage} />
      ) : (
        <Box sx={styles.attachmentFile}>
          <PdfIcon sx={{ fontSize: "20px" }} />
          <Typography sx={styles.attachmentName} dir="auto">
            {attachment.name}
          </Typography>
        </Box>
      )}
      {attachment.uploading && <Box sx={styles.attachmentBusy} role="progressbar" aria-label={t("attach.uploading")} />}
      {onRemove && (
        <Tooltip title={t("attach.remove")}>
          <IconButton size="small" aria-label={t("attach.remove")} onClick={() => onRemove(attachment)} sx={styles.attachmentRemove}>
            <RemoveIcon sx={{ fontSize: "14px" }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}
